// frontend/src/settings/StaffCsvImportModal.jsx
// スタッフを CSV で一括登録するモーダル。
//   フロー: ファイル選択 → パース+検証 → プレビュー → 1 行ずつ addStaff() → 結果サマリ
//
// CSV 列 (ヘッダー):
//   社員番号, 姓, 名, メールアドレス, 入職年, 権限, 職種
//   (職種は jobTypes.name と前方一致で lookup、無ければ未設定)
//   権限は "admin" or "staff" (未指定は staff)

import { useState, useRef } from 'react';
import Papa from 'papaparse';
import { Upload, Download, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { addStaff, supabase } from '../supabase';

const CSV_TEMPLATE = `社員番号,姓,名,メールアドレス,入職年,権限,職種
001,山田,太郎,yamada@example.com,2020,staff,看護師
002,佐藤,花子,sato@example.com,2015,admin,看護師
`;

const HEADER_ALIASES = {
  employee_id: ['社員番号', '職員番号', 'employee_id', 'employeeId', 'id'],
  last_name:   ['姓', 'last_name', 'lastName', '名字'],
  first_name:  ['名', 'first_name', 'firstName', '下の名前'],
  email:       ['メールアドレス', 'メール', 'email', 'mail'],
  year:        ['入職年', '年次', 'year'],
  role:        ['権限', 'role'],
  job_type:    ['職種', '職種名', 'job_type', 'jobType'],
};

const resolveHeader = (headerRow) => {
  // headerRow: array of strings. Returns { employee_id: idx|null, ... }
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
    const idx = headerRow.findIndex((h) =>
      aliases.some((a) => (h || '').trim().toLowerCase() === a.toLowerCase())
    );
    map[key] = idx >= 0 ? idx : null;
  });
  return map;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function StaffCsvImportModal({
  open,
  onClose,
  onSuccess,
  organizationId,
  existingEmployeeIds = [],
  jobTypes = [],
}) {
  const [stage, setStage] = useState('select'); // select / preview / importing / done
  const [rows, setRows] = useState([]);          // [{raw, valid, errors, data}]
  const [parseError, setParseError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);    // [{employeeId, name, ok, message, initialPassword}]
  const fileInputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setStage('select');
    setRows([]);
    setParseError('');
    setProgress({ done: 0, total: 0 });
    setResults([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (stage === 'importing') return; // 実行中は閉じない
    reset();
    onClose?.();
  };

  const handleDownloadTemplate = () => {
    const bom = '﻿'; // Excel 互換のため BOM
    const blob = new Blob([bom + CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'staff_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const jobTypeByName = (() => {
    const map = new Map();
    jobTypes.forEach((jt) => map.set((jt.name || '').trim(), jt));
    return map;
  })();

  const handleFile = (file) => {
    if (!file) return;
    setParseError('');
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const data = result.data || [];
          if (data.length < 2) {
            setParseError('CSV にヘッダー行と 1 件以上のデータが必要です');
            return;
          }
          const headerRow = data[0];
          const headerMap = resolveHeader(headerRow);
          if (headerMap.employee_id === null || headerMap.last_name === null ||
              headerMap.first_name === null || headerMap.email === null) {
            setParseError('必須列 (社員番号 / 姓 / 名 / メールアドレス) が見つかりません');
            return;
          }
          const seenEmpIds = new Set();
          const seenEmails = new Set();
          const parsed = data.slice(1).map((cols, i) => {
            const get = (key) => (headerMap[key] !== null ? (cols[headerMap[key]] || '').trim() : '');
            const employee_id = get('employee_id');
            const last_name = get('last_name');
            const first_name = get('first_name');
            const email = get('email');
            const year = get('year');
            const roleRaw = (get('role') || 'staff').toLowerCase();
            const role = roleRaw === 'admin' ? 'admin' : 'staff';
            const jobTypeName = get('job_type');
            const jobType = jobTypeName ? jobTypeByName.get(jobTypeName) : null;

            const errors = [];
            if (!employee_id) errors.push('社員番号 必須');
            if (!last_name) errors.push('姓 必須');
            if (!first_name) errors.push('名 必須');
            if (!email) errors.push('メール 必須');
            if (email && !EMAIL_RE.test(email)) errors.push('メール形式が不正');
            if (employee_id && existingEmployeeIds.includes(employee_id)) {
              errors.push('この部署に同じ社員番号が既に存在');
            }
            if (employee_id && seenEmpIds.has(employee_id)) {
              errors.push('CSV 内で社員番号が重複');
            }
            if (email && seenEmails.has(email.toLowerCase())) {
              errors.push('CSV 内でメールが重複');
            }
            if (jobTypeName && !jobType) {
              errors.push(`職種「${jobTypeName}」がマスタに未登録`);
            }

            if (employee_id) seenEmpIds.add(employee_id);
            if (email) seenEmails.add(email.toLowerCase());

            return {
              rowNumber: i + 2, // 1-indexed + header
              valid: errors.length === 0,
              errors,
              data: {
                employee_id, last_name, first_name, email, year, role,
                job_type_id: jobType?.id || null,
                job_type_name: jobType?.name || jobTypeName || '',
              },
            };
          });
          setRows(parsed);
          setStage('preview');
        } catch (e) {
          setParseError('CSV の解析に失敗しました: ' + e.message);
        }
      },
      error: (e) => {
        setParseError('CSV の読み込みに失敗しました: ' + e.message);
      },
    });
  };

  const validCount = rows.filter((r) => r.valid).length;
  const invalidCount = rows.length - validCount;

  const runImport = async () => {
    const targets = rows.filter((r) => r.valid);
    if (targets.length === 0) return;
    setStage('importing');
    setProgress({ done: 0, total: targets.length });
    const outcomes = [];
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      const { employee_id, last_name, first_name, email, year, role, job_type_id } = r.data;
      try {
        const staffRow = await addStaff(
          employee_id, last_name, first_name, email, year, role, organizationId
        );
        // 職種を別途更新 (addStaff は job_type を扱わない)
        if (job_type_id && staffRow?.id) {
          try {
            await supabase.from('staff')
              .update({ job_type_id })
              .eq('id', staffRow.id);
          } catch (e) {
            console.warn('job_type update failed (non-fatal):', e);
          }
        }
        outcomes.push({
          rowNumber: r.rowNumber,
          employeeId: employee_id,
          name: `${last_name} ${first_name}`,
          ok: true,
          message: '登録成功',
        });
      } catch (e) {
        outcomes.push({
          rowNumber: r.rowNumber,
          employeeId: employee_id,
          name: `${last_name} ${first_name}`,
          ok: false,
          message: e.message || '不明なエラー',
        });
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setResults(outcomes);
    setStage('done');
    const successCount = outcomes.filter((o) => o.ok).length;
    if (successCount > 0) onSuccess?.(successCount);
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            <Upload size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            CSV でスタッフを一括登録
          </h3>
          <button onClick={handleClose} style={styles.closeBtn} disabled={stage === 'importing'}>
            <X size={20} />
          </button>
        </div>

        {stage === 'select' && (
          <div style={styles.body}>
            <p style={styles.helper}>
              CSV ファイル (UTF-8 / Shift_JIS) を選択してください。<br />
              必須列: 社員番号 / 姓 / 名 / メールアドレス<br />
              任意列: 入職年 / 権限 (admin|staff) / 職種 (職種マスタ名)
            </p>
            <button onClick={handleDownloadTemplate} style={styles.ghostBtn}>
              <Download size={14} />テンプレートをダウンロード
            </button>
            <div style={{ marginTop: 20 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={styles.fileInput}
              />
            </div>
            {parseError && (
              <div style={styles.errorBox}>
                <AlertCircle size={14} /> {parseError}
              </div>
            )}
          </div>
        )}

        {stage === 'preview' && (
          <div style={styles.body}>
            <div style={styles.summary}>
              <span>総行数: <strong>{rows.length}</strong></span>
              <span style={{ color: '#16a34a' }}>有効: <strong>{validCount}</strong></span>
              <span style={{ color: '#dc2626' }}>エラー: <strong>{invalidCount}</strong></span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>行</th>
                    <th style={styles.th}>社員番号</th>
                    <th style={styles.th}>氏名</th>
                    <th style={styles.th}>メール</th>
                    <th style={styles.th}>入職年</th>
                    <th style={styles.th}>権限</th>
                    <th style={styles.th}>職種</th>
                    <th style={styles.th}>検証</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={r.valid ? styles.rowOk : styles.rowNg}>
                      <td style={styles.td}>{r.rowNumber}</td>
                      <td style={styles.td}>{r.data.employee_id}</td>
                      <td style={styles.td}>{r.data.last_name} {r.data.first_name}</td>
                      <td style={styles.td}>{r.data.email}</td>
                      <td style={styles.td}>{r.data.year}</td>
                      <td style={styles.td}>{r.data.role}</td>
                      <td style={styles.td}>{r.data.job_type_name || '-'}</td>
                      <td style={styles.td}>
                        {r.valid
                          ? <span style={styles.ok}><Check size={12} /> OK</span>
                          : <span style={styles.ng} title={r.errors.join(' / ')}>
                              <AlertCircle size={12} /> {r.errors[0]}
                              {r.errors.length > 1 && ` (+${r.errors.length - 1})`}
                            </span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={styles.footer}>
              <button onClick={() => { reset(); }} style={styles.secondaryBtn}>戻る</button>
              <button
                onClick={runImport}
                disabled={validCount === 0}
                style={{
                  ...styles.primaryBtn,
                  opacity: validCount === 0 ? 0.5 : 1,
                  cursor: validCount === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <Upload size={14} />
                有効な {validCount} 件を登録する
              </button>
            </div>
          </div>
        )}

        {stage === 'importing' && (
          <div style={styles.body}>
            <div style={styles.progressWrap}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#4338ca' }} />
              <div style={{ marginTop: 12, fontSize: '1.1rem', fontWeight: 600 }}>
                登録中... {progress.done} / {progress.total}
              </div>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${(progress.done / progress.total) * 100}%`,
                }} />
              </div>
            </div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {stage === 'done' && (
          <div style={styles.body}>
            <div style={styles.summary}>
              <span style={{ color: '#16a34a' }}>成功: <strong>{results.filter((r) => r.ok).length}</strong></span>
              <span style={{ color: '#dc2626' }}>失敗: <strong>{results.filter((r) => !r.ok).length}</strong></span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>行</th>
                    <th style={styles.th}>社員番号</th>
                    <th style={styles.th}>氏名</th>
                    <th style={styles.th}>結果</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} style={r.ok ? styles.rowOk : styles.rowNg}>
                      <td style={styles.td}>{r.rowNumber}</td>
                      <td style={styles.td}>{r.employeeId}</td>
                      <td style={styles.td}>{r.name}</td>
                      <td style={styles.td}>
                        {r.ok
                          ? <span style={styles.ok}><Check size={12} /> {r.message}</span>
                          : <span style={styles.ng}><AlertCircle size={12} /> {r.message}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={styles.footer}>
              <button onClick={reset} style={styles.secondaryBtn}>別の CSV を読み込む</button>
              <button onClick={handleClose} style={styles.primaryBtn}>閉じる</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10002, padding: '1rem',
  },
  card: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 820,
    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
  },
  title: { margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' },
  closeBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: '#6b7280', padding: 4,
  },
  body: { padding: '18px 20px', overflowY: 'auto', flex: 1 },
  helper: { fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.6, margin: '0 0 1rem 0' },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff',
    borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#374151',
  },
  fileInput: {
    width: '100%', padding: '12px', border: '2px dashed #cbd5e1',
    borderRadius: 8, background: '#f8fafc', cursor: 'pointer',
  },
  errorBox: {
    marginTop: 12, padding: '10px 14px', background: '#fef2f2',
    border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 6,
    display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem',
  },
  summary: {
    display: 'flex', gap: 20, padding: '10px 14px', background: '#f8fafc',
    borderRadius: 6, marginBottom: 12, fontSize: '0.9rem',
  },
  tableWrap: { overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: {
    padding: '8px 10px', textAlign: 'left', background: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#475569',
    position: 'sticky', top: 0,
  },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  rowOk: { background: '#fff' },
  rowNg: { background: '#fef2f2' },
  ok: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    color: '#16a34a', fontWeight: 600, fontSize: '0.8rem',
  },
  ng: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    color: '#dc2626', fontWeight: 600, fontSize: '0.8rem',
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    padding: '12px 0 0 0', borderTop: '1px solid #f3f4f6', marginTop: 14,
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
    color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
    fontSize: '0.9rem', fontWeight: 600,
  },
  secondaryBtn: {
    padding: '8px 16px', background: '#f3f4f6', color: '#374151',
    border: 'none', borderRadius: 8, cursor: 'pointer',
    fontSize: '0.9rem', fontWeight: 500,
  },
  progressWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '32px 0',
  },
  progressBar: {
    marginTop: 14, width: '100%', maxWidth: 400,
    height: 10, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: 'linear-gradient(90deg, #4338ca, #6366f1)',
    transition: 'width 0.2s ease',
  },
};
